export const CUSTOM_MIGRATION_SECTIONS = [
  { id: "main", label: "Main records" },
  { id: "other", label: "Other records" },
];

const template = (path, text, fileName) => ({ path, text, fileName });

export const CUSTOM_MIGRATION_STEPS = [
  {
    id: "productType",
    section: "main",
    label: "Insurance Classes",
    description: "Update or create an insurance class by code.",
    behavior: "Updates or creates by code",
    apiEntity: "productType",
    templates: [
      template(
        "productType/downloadTemplate",
        "Download Blank Template",
        "product-type-template"
      ),
      template(
        "productType/downloadExistingTemplate",
        "Download Existing Product Types",
        "product-type-existing"
      ),
    ],
  },
  {
    id: "provider",
    section: "main",
    label: "Insurers",
    description:
      "Update or create an insurer by code. Blank cells keep the stored value.",
    behavior: "Updates or creates by code",
    apiEntity: "provider",
    templates: [
      template(
        "provider/downloadTemplate",
        "Download Blank Template",
        "provider-template"
      ),
      template(
        "provider/downloadExistingTemplate",
        "Download Existing Providers",
        "provider-existing"
      ),
    ],
  },
  {
    id: "user",
    section: "main",
    label: "Agents / Producers",
    description: "Update or create a user by staff ID.",
    behavior: "Updates or creates by staff ID",
    apiEntity: "user",
    templates: [
      template("user/downloadTemplate", "Download Blank Template", "user-template"),
      template(
        "user/downloadExistingTemplate",
        "Download Existing Users",
        "user-existing"
      ),
    ],
  },
  {
    id: "client",
    section: "main",
    label: "Clients",
    description: "Update or create a client by code.",
    behavior: "Updates or creates by code",
    apiEntity: "client",
    templates: [
      template("client/downloadTemplate", "Download Template", "client-template"),
    ],
  },
  {
    id: "giOrder",
    section: "main",
    label: "GI Orders",
    description:
      "Import GI policies as debit notes. A confirmed row creates or updates the debit note and the linked order with isGI true. Import classes, insurers, agents, and clients first.",
    behavior: "Updates or creates by code",
    apiEntity: "debitNote",
    templates: [
      template(
        "debitNote/downloadTemplate",
        "Download Template",
        "debit-note-template"
      ),
    ],
  },
  {
    id: "lifeOrder",
    section: "main",
    label: "Life Orders",
    description:
      "Update or create a life order by code. Import products, agents, and clients first. This step saves the order with isGI false.",
    behavior: "Updates or creates by code as life",
    apiEntity: "order",
    importData: { isGI: false },
    templates: [
      template("order/downloadTemplate", "Download Template", "order-template"),
    ],
  },
  {
    id: "product",
    section: "other",
    label: "Products",
    description:
      "Update or create a product by code. Import this before life orders and GI orders.",
    behavior: "Updates or creates by code",
    apiEntity: "product",
    templates: [
      template(
        "product/downloadTemplate",
        "Download Blank Template",
        "product-template"
      ),
      template(
        "product/downloadExistingTemplate",
        "Download Existing Products",
        "product-existing"
      ),
    ],
  },
  {
    id: "policyInsured",
    section: "other",
    label: "Policy Insured",
    description:
      "Add insured people to an order found by order code. An unknown order code is skipped.",
    behavior: "Updates an existing order",
    importKind: "policyInsured",
    templates: [
      template(
        "order/downloadPolicyInsuredTemplate",
        "Download Template",
        "policy-insured-template"
      ),
    ],
  },
  {
    id: "policyBeneficiary",
    section: "other",
    label: "Policy Beneficiary",
    description:
      "Add beneficiaries to an order found by order code. An unknown order code is skipped.",
    behavior: "Updates an existing order",
    importKind: "policyBeneficiary",
    templates: [
      template(
        "order/downloadPolicyBeneficiaryTemplate",
        "Download Template",
        "policy-beneficiary-template"
      ),
    ],
  },
  {
    id: "orderService",
    section: "other",
    label: "Order Services",
    description: "Update or create an order service by code.",
    behavior: "Updates or creates by code",
    apiEntity: "orderService",
    templates: [
      template(
        "orderService/downloadTemplate",
        "Download Template",
        "order-service-template"
      ),
    ],
  },
  {
    id: "cpd",
    section: "other",
    label: "CPD",
    description: "Each import creates new CPD records.",
    behavior: "Always creates a new record",
    apiEntity: "cpd",
    templates: [
      template("cpd/downloadTemplate", "Download Template", "cpd-template"),
    ],
  },
  {
    id: "incentive",
    section: "other",
    label: "Incentives",
    description:
      "A row with a code updates that incentive. A row without a code creates one.",
    behavior: "Updates when a code is present, otherwise creates",
    apiEntity: "incentive",
    templates: [
      template(
        "incentive/downloadTemplate",
        "Download Template",
        "incentive-template"
      ),
    ],
  },
  {
    id: "bankTransaction",
    section: "other",
    label: "Bank Transactions",
    description:
      "Each import creates new rows on the selected bank account. Choose the account before importing.",
    behavior: "Always creates new rows",
    apiEntity: "bankTransaction",
    requiresBankAccount: true,
    templates: [
      template(
        "bankTransaction/downloadTemplate",
        "Download Template",
        "bank-transaction-template"
      ),
    ],
  },
  {
    id: "userRole",
    section: "other",
    label: "User Role Access Rights",
    description:
      "Updates roles that already exist. An unknown role column is skipped and is not created.",
    behavior: "Updates existing roles only",
    apiEntity: "userRole",
    templates: [
      template(
        "userRole/downloadTemplate",
        "Download Template",
        "user-role-template"
      ),
    ],
  },
  {
    id: "providerLogin",
    section: "other",
    label: "Insurer Portal Logins",
    description:
      "Insert appends logins. Replace overwrites logins for each insurer in the file. An unknown insurer code is not created.",
    behavior: "Insert or replace per insurer",
    importKind: "providerLogin",
    importModes: [
      { value: "insert", label: "Insert (append)" },
      { value: "replace", label: "Replace (overwrite per insurer)" },
    ],
    templates: [
      template(
        "provider/downloadAllLoginTemplate",
        "Download Blank Template",
        "provider-login-template"
      ),
      template(
        "provider/downloadAllLoginExistingTemplate",
        "Download Existing Logins",
        "provider-login-existing"
      ),
    ],
  },
  {
    id: "providerContact",
    section: "other",
    label: "Insurer Contacts",
    description:
      "Insert appends contacts. Replace overwrites contacts for each insurer in the file. An unknown insurer code is not created.",
    behavior: "Insert or replace per insurer",
    importKind: "providerContact",
    importModes: [
      { value: "insert", label: "Insert (append)" },
      { value: "replace", label: "Replace (overwrite per insurer)" },
    ],
    templates: [
      template(
        "provider/downloadAllContactTemplate",
        "Download Blank Template",
        "provider-contact-template"
      ),
      template(
        "provider/downloadAllContactExistingTemplate",
        "Download Existing Contacts",
        "provider-contact-existing"
      ),
    ],
  },
];

export const stepsForSection = (sectionId) =>
  CUSTOM_MIGRATION_STEPS.filter((step) => step.section === sectionId);
